import type { TransferRouteConfig } from "./schemas";
import { isInsideTransferZone, MARRAKECH_CENTRE, metresBetween, type TransferPoint } from "./zone";

/**
 * How far from the middle of Marrakech a pinned place may be, in metres,
 * straight line. The Palmeraie, the Ourika road, Tamansourt and the Agafay
 * camps (about 30 to 40 km) all fit; a club in Casablanca or a riad in
 * Essaouira does not, and must not be sold as a city ride plus 100 MAD.
 */
export const TRANSFER_MAX_DISTANCE_M = 60_000;

/** True when a pinned place is too far from Marrakech for any transfer route. A typed place is never judged here. */
export function isTooFarForTransfer(p: TransferFeePoint | null | undefined): boolean {
  if (p == null || !isPinned(p)) return false;
  return metresBetween(MARRAKECH_CENTRE, p) > TRANSFER_MAX_DISTANCE_M;
}

/**
 * A ride back whose clock time is not after the pick-up is the next day
 * (pick-up 22:30, back at 03:00). The booking stores times only, so this is
 * the one rule every surface reads to say "next day".
 */
export function transferReturnIsNextDay(pickupTime: string | null | undefined, returnTime: string | null | undefined): boolean {
  const toMin = (t: string | null | undefined) => {
    const m = t ? /^(\d{2}):(\d{2})$/.exec(t) : null;
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const a = toMin(pickupTime);
  const b = toMin(returnTime);
  return a !== null && b !== null && b <= a;
}

/**
 * Charged once per booking when the accommodation, restaurant or club is
 * outside the zone. Pass the points through `transferFeePlaces` first.
 */
export const TRANSFER_OUTSIDE_FEE = 100;

/**
 * The largest vehicle seats 17. Bigger groups go to WhatsApp.
 * Must match the largest vehicle package seeded for transfer routes
 * (the 17-seat minibus); update both together if the fleet changes.
 */
export const TRANSFER_MAX_GUESTS = 17;

/**
 * A place that can count toward the fee. A typed place (`typed: true`, no pin)
 * never adds it: the team checks it on WhatsApp instead.
 */
export interface TransferFeePoint {
  lat?: number;
  lng?: number;
  typed?: boolean;
}

function isPinned(p: TransferFeePoint): p is TransferPoint {
  return p.typed !== true && Number.isFinite(p.lat) && Number.isFinite(p.lng);
}

export function transferOutsideFee(places: ReadonlyArray<TransferFeePoint | null | undefined>): number {
  return places.some((p) => p != null && isPinned(p) && !isInsideTransferZone(p)) ? TRANSFER_OUTSIDE_FEE : 0;
}

/** True when any of the given places was typed by the guest rather than picked. */
export function transferHasTypedPlace(places: ReadonlyArray<TransferFeePoint | null | undefined>): boolean {
  return places.some((p) => p?.typed === true);
}

/**
 * The places that can trigger the outside fee. The pick-up (the customer's
 * accommodation) always counts; the second place counts only when it is a
 * restaurant or a club. The Agafay camp never does (owner, 2026-09-23: the
 * Agafay price already covers the desert), and a place on a route that takes
 * none is ignored. A typed place passes through but adds no fee
 * (see `transferOutsideFee`).
 */
export function transferFeePlaces<P extends TransferFeePoint>(
  secondPlace: TransferRouteConfig["secondPlace"],
  pickup: P | null | undefined,
  place: P | null | undefined,
): Array<P | null | undefined> {
  return secondPlace === "restaurant" || secondPlace === "club" ? [pickup, place] : [pickup];
}

/**
 * The smallest vehicle that seats `guests`, or null when none does.
 * A non-integer `guests` is treated like `guests < 1` (returns null).
 * On a tie of `maxGuests` between packages, the first one in input order wins.
 */
export function pickTransferPackage<P extends { maxGuests: number | null }>(
  packages: readonly P[],
  guests: number,
): P | null {
  if (guests < 1 || !Number.isInteger(guests)) return null;
  const fitting = packages
    .filter((p): p is P & { maxGuests: number } => p.maxGuests != null && p.maxGuests >= guests)
    .sort((a, b) => a.maxGuests - b.maxGuests);
  return fitting[0] ?? null;
}
